import { tokenManager } from "./token-manager";
import { triggerLogout } from "./logoutNotifier";
import { countMetric, captureApiException } from "shared/utils/sentryHelpers";
import { DEFAULT_AUTH_PREFIX } from "./constants";

const TOKEN_GETTERS = {
  access: () => tokenManager.getAccess(),
  guest: () => tokenManager.getGuest(),
  refresh: () => tokenManager.getRefresh(),
};

export function getBaseUrl() {
  return process.env.REACT_APP_SERVER_URL_V2 || "";
}

export function getFullUrl(url, baseUrl) {
  if (url.startsWith("//") || /^https?:\/\//i.test(url)) return url;
  const base = baseUrl.replace(/\/$/, "");
  const path = url.replace(/^\//, "");
  return path ? `${base}/${path}` : base || "/";
}

export function buildHeaders(config) {
  const {
    headers = {},
    body,
    method,
    tokenType = "access",
    authPrefix = DEFAULT_AUTH_PREFIX,
  } = config;
  const finalHeaders = { ...headers };

  const token = TOKEN_GETTERS[tokenType]?.();
  if (token) finalHeaders.Authorization = `${authPrefix} ${token}`;

  if (!(body instanceof FormData) && body && method !== "GET") {
    finalHeaders["Content-Type"] = "application/json";
  }

  return finalHeaders;
}

export function successResponse(processedResponse) {
  return {
    success: true,
    data: processedResponse.data,
    statusCode: processedResponse.status,
    ok: true,
  };
}

// Promise-based refresh lock: one refresh runs; all 401s await the same promise (no race between check and subscribe).
// Refresh request uses passed meta so _retryCount is consistent; depends on executeRequest merging config.meta upstream.
let refreshPromise = null;

function getRefreshPromise(runTransport, refreshMeta) {
  if (typeof runTransport !== "function") {
    return Promise.reject(new Error("runTransport missing"));
  }
  if (refreshPromise) return refreshPromise;
  refreshPromise = runTransport("/users/login/refresh", {
    method: "POST",
    tokenType: "refresh",
    meta: refreshMeta,
  })
    .then((refreshRes) => {
      const ok = refreshRes?.status >= 200 && refreshRes?.status < 300;
      const newToken = refreshRes?.data?.access_token;
      if (ok && newToken) {
        // TOKEN_PERSIST_DUMMY: HTTP client refresh interceptor — access (tokenManager)
        tokenManager.setAccess(newToken);
        return { newToken };
      }
      return Promise.reject(
        Object.assign(new Error("Refresh failed"), {
          response: { status: 401, data: {} },
          __refreshFailure: true,
        })
      );
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

export function getHeader(response, name) {
  const h = response?.headers;
  if (!h) return "";
  if (typeof h.get === "function") return h.get(name) || "";
  const key = name?.toLowerCase?.() ?? name;
  return h[key] || h[name] || "";
}

export function toOurRequestShape(axiosConfig) {
  return {
    url: axiosConfig.url,
    headers: axiosConfig.headers || {},
    body: axiosConfig.data,
    method: axiosConfig.method || "GET",
    props: axiosConfig.props || {},
    tokenType: axiosConfig.tokenType,
    authPrefix: axiosConfig.authPrefix,
  };
}

export function fromOurRequestShape(ourResult, axiosConfig) {
  return {
    ...axiosConfig,
    url: ourResult.url,
    headers: ourResult.headers,
    data: ourResult.body ?? axiosConfig.data,
  };
}

export function performLogout(reason) {
  tokenManager.clearAll();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("permission-refetch"));
  }
  triggerLogout(reason);
}

export function logoutOnAuthError(formattedError) {
  if (!formattedError || formattedError.success !== false) return formattedError;
  const isAuthError =
    formattedError.statusCode === 401 || formattedError.error?.redirectToLogin === true;
  if (isAuthError) {
    const reason = formattedError.sessionExpired ? { sessionExpired: true } : undefined;
    performLogout(reason);
  }
  return formattedError;
}

export async function trackApiErrorToSentry(originalError, formattedError, options = {}) {
  const { apiUrl, apiMethod, duration } = options;
  const status = formattedError?.statusCode ?? originalError?.response?.status;
  const eventType =
    formattedError?.error?.type === "network_error"
      ? "network_error"
      : formattedError?.error?.type === "parse_error"
        ? "parse_error"
        : "api_error";
  try {
    countMetric("total_error_api_calls", 1);
    captureApiException(originalError, {
      level: "error",
      tags: {
        event_type: eventType,
        api_url: apiUrl || "unknown",
        api_method: apiMethod || "unknown",
        api_status: status ?? "unknown",
        api_duration_ms: duration ?? 0,
      },
      extra: {
        formatted_error: formattedError,
        error_response: originalError?.response?.data,
      },
    });
  } catch (_) {
    // Sentry not available or misconfigured
  }
}

/**
 * Run refresh once; all concurrent 401s await the same promise. Retry with new token.
 * Requires executeRequest to merge config.meta (so _retryCount survives) — see index.js.
 * Refresh failure rejects with 401-shaped error → errorInterceptor → logoutOnAuthError (single logout).
 */
export function runRefreshAndRetry(url, config, runTransport, options = {}) {
  const nextRetryCount = (options._retryCount ?? config._retryCount ?? 0) + 1;
  const retryMeta = { ...options, _retry: true, _retryCount: nextRetryCount };

  return getRefreshPromise(runTransport, retryMeta)
    .then(() =>
      runTransport(url, {
        ...config,
        tokenType: "access",
        meta: retryMeta,
      })
    )
    .catch((err) => {
      const out = err?.response
        ? err
        : Object.assign(err instanceof Error ? err : new Error("Refresh failed"), {
            response: { status: 401, data: {} },
          });
      if (err?.__refreshFailure) out.__refreshFailure = true;
      return Promise.reject(out);
    });
}
